//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import DateString from '@models/DateString';
import TimeZoneId from '@models/TimeZoneId';
import { isDefined } from '@utils/filterPredicates';
import { getDatesInRange } from '@utils/getDatesInRange';
import { normalizeToMap } from '@utils/normalize';

interface DailyMetrics {
  date: DateString;
  metrics: object;
}

interface Params<T extends DailyMetrics> {
  days: T[];
  emptyMetrics: T['metrics'];
  end: DateString;
  start: DateString;
  timezone: TimeZoneId;
}

export const fillInMissingEngagementDates = <T extends DailyMetrics>({
  days,
  emptyMetrics,
  end,
  start,
  timezone,
}: Params<T>): T[] => {
  const { byId: existingDaysMap } = normalizeToMap(days, (day) => day.date);

  const startDate = DateString.toDate(start, timezone);
  const endDate = DateString.toDate(end, timezone);

  return getDatesInRange(startDate, endDate, { inclusive: true }).map(
    (date) => {
      const dateKey = DateString.fromTimestamp(date, timezone);
      const existingDay = existingDaysMap.get(dateKey);

      return isDefined(existingDay)
        ? existingDay
        : ({
            date: dateKey,
            metrics: emptyMetrics,
          } as T);
    },
  );
};
